'use memo';

import { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import {
  AgxRow,
  AgxColumn,
  AgxToast,
  AgxDivider,
  Option,
  AgxRadioGroup,
  useDebouncedState,
} from '@urbanx/agx-ui-components';
import { AgxSpacer } from '@urbanx/agx.ui-components2';
import { AuthContext } from '@urbanx/agx.auth.client';
import { LicenceDetailsState } from '@urbanx/agx.agencies.client';
import {
  CampaignActionRequestCampaignAction,
  CampaignAdminModelMarketingStatus,
  CampaignAdminModelStage,
  StaffBeginCampaignRequestState,
  usePerformCampaignAction,
  useStaffBeginCampaign,
} from '@urbanx/agx.campaigns.client';
import { useParams, useNavigate } from 'react-router-dom';
import { ContentLayout, PageLayout } from 'layout';
import { useAgentsInAgency, useCampaignsList, useSelectedAgency } from 'hooks';
import { AgxToastState } from 'types/commonTypes';
import { CampaignsTab, LaunchFilterOptions } from 'types/campaigns';
import { MenuTitles, Pages } from 'constants/menu';
import Popup from 'components/Popup/Popup';
import { CampaignsTable } from './CampaignsTable';
import { CampaignsFilter } from './CampaignsFilter';
import { getStoredCampaignsFilterState, storeCampaignsFilterState } from './CampaignFilterState';
import './Campaigns.scss';

const CampaignsPage = () => {
  const { agencyId } = useParams();
  const [currentTab, setCurrentTab] = useState(CampaignsTab.All);
  const [searchText, setSearchText] = useDebouncedState<string>('', 300);
  const [statusFilter, setStatusFilter] = useState<CampaignAdminModelStage[] | undefined>();
  const [agentsFilter, setAgentsFilter] = useState<string[] | undefined>();
  const [marketingFilter, setMarketingFilter] = useState<
    CampaignAdminModelMarketingStatus[] | undefined
  >();
  const [contractFilter, setContractFilter] = useState<string[] | undefined>();
  const [launchFilter, setLaunchFilter] = useState<LaunchFilterOptions | undefined>();

  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const { userAccount } = useContext(AuthContext);
  const { agency: selectedAgency } = useSelectedAgency();
  const { data: agents } = useAgentsInAgency();
  const navigate = useNavigate();
  const { campaigns, archivedCampaigns, isLoadingCampaigns, isLoadingArchivedCampaigns } =
    useCampaignsList({
      currentTab,
      searchText,
      statusFilter,
      agentsFilter,
      marketingFilter,
      contractFilter,
      launchFilter,
    });
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAustralianState, setSelectedAustralianState] = useState<Option | undefined>();

  useEffect(() => {
    const storedState = getStoredCampaignsFilterState(agencyId ?? '');
    if (storedState) {
      setStatusFilter(storedState.statusFilter);
      setAgentsFilter(storedState.agentsFilter);
      setMarketingFilter(storedState.marketingFilter);
      setContractFilter(storedState.contractFilter);
      setLaunchFilter(storedState.launchFilter);
    }
  }, []);

  useEffect(() => {
    storeCampaignsFilterState(agencyId ?? '', {
      statusFilter,
      agentsFilter,
      marketingFilter,
      contractFilter,
      launchFilter,
    });
  }, [
    currentTab,
    searchText,
    statusFilter,
    agentsFilter,
    marketingFilter,
    contractFilter,
    launchFilter,
  ]);

  const selectedAgencyStatesOptions = useMemo(() => {
    return selectedAgency?.companyDetails?.licences
      ? selectedAgency.companyDetails.licences.map(
          (license) =>
            ({
              label: license.state,
              value: license.state,
            }) as Option
        )
      : [];
  }, [selectedAgency]);

  const { staffBeginCampaign, staffBeginCampaignPending } = useStaffBeginCampaign({
    onSuccess: (response) => {
      const campaignId = response?.campaignId;
      if (campaignId) {
        if (!isLoadingCampaigns) {
          navigate(
            `/${Pages.AGENCIES}/${agencyId}/campaigns/${campaignId}/${Pages.PROPERTY_INFORMATION}`
          );
        }
      }
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error begin campaign',
        open: true,
      });
    },
  });

  const toastMessage = (action?: CampaignActionRequestCampaignAction) => {
    switch (action) {
      case CampaignActionRequestCampaignAction.Archive:
        return 'Campaign moved to Archive';
      case CampaignActionRequestCampaignAction.Restore:
        return 'Campaign restored successfully';
      case CampaignActionRequestCampaignAction.MarkAsSold:
        return 'Campaign status updated to Sold';
      case CampaignActionRequestCampaignAction.MarkAsUnderContract:
        return 'Campaign status updated to Under Contract';
      case CampaignActionRequestCampaignAction.Withdraw:
        return 'Campaign status updated to Withdrawn';
      case CampaignActionRequestCampaignAction.RevertToCurrent:
        return 'Campaign status updated to Current';
      default:
        return 'Campaign action performed successfully';
    }
  };

  const { performCampaignAction } = usePerformCampaignAction({
    onSuccess: (_, { campaignAction }) => {
      updateToastState({
        color: 'success',
        message: toastMessage(campaignAction),
        open: true,
      });
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error performing campaign action',
        open: true,
      });
    },
  });

  const onPerformCampaignAction = useCallback(
    (campaignId: string, campaignAction: CampaignActionRequestCampaignAction) => {
      performCampaignAction({
        campaignId,
        campaignAction,
      });
    },
    [performCampaignAction]
  );

  const onBeginCampaign = (state: LicenceDetailsState) => {
    if (!agencyId) return;

    staffBeginCampaign({
      agencyId: agencyId,
      state: state as unknown as StaffBeginCampaignRequestState,
    });
  };

  const onNewCampaignClicked = useCallback(() => {
    if (selectedAgencyStatesOptions.length > 1) {
      setShowPopup(true);
    } else {
      setSelectedAustralianState(selectedAgencyStatesOptions[0]);
      onBeginCampaign(
        selectedAgency?.companyDetails?.licences?.[0]?.state ?? LicenceDetailsState.QLD
      );
    }
  }, [selectedAgencyStatesOptions, setShowPopup, setSelectedAustralianState, onBeginCampaign]);

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.CAMPAIGNS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.CAMPAIGNS}
        disableContainerRoundedCorners
      >
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn>
          <AgxSpacer className="stickyFilterContainer">
            <AgxSpacer className="stickyFilter">
              <AgxSpacer
                fill
                contentJustification="space-between"
                contentAlignment="center"
                direction="horizontal"
                padding="md"
                className="campaignManagementFilters"
              >
                <CampaignsFilter
                  selectedTab={currentTab}
                  statusFilter={statusFilter}
                  agentsFilter={agentsFilter}
                  marketingFilter={marketingFilter}
                  contractFilter={contractFilter}
                  launchFilter={launchFilter}
                  staffBeginCampaignPending={staffBeginCampaignPending}
                  agents={agents ?? []}
                  setCurrentTab={setCurrentTab}
                  setSearchText={setSearchText}
                  setStatusFilter={setStatusFilter}
                  setAgentsFilter={setAgentsFilter}
                  setMarketingFilter={setMarketingFilter}
                  setContractFilter={setContractFilter}
                  setLaunchFilter={setLaunchFilter}
                  onNewCampaignClicked={onNewCampaignClicked}
                />
              </AgxSpacer>
              <AgxDivider />
            </AgxSpacer>
          </AgxSpacer>
          <AgxRow extraClasses="campaignsTableRow">
            {currentTab === CampaignsTab.All && (
              <CampaignsTable
                agencyId={agencyId ?? ''}
                campaigns={campaigns}
                agents={agents ?? []}
                isLoading={isLoadingCampaigns}
                isArchivedTab={false}
                onPerformCampaignAction={onPerformCampaignAction}
              />
            )}
            {currentTab === CampaignsTab.Archived && (
              <CampaignsTable
                agencyId={agencyId ?? ''}
                campaigns={archivedCampaigns}
                agents={agents ?? []}
                isLoading={isLoadingArchivedCampaigns}
                isArchivedTab={true}
                onPerformCampaignAction={onPerformCampaignAction}
              />
            )}
          </AgxRow>
        </AgxColumn>
      </ContentLayout>
      {showPopup ? (
        <Popup
          title="Select the state the property is located in:"
          isOpen={showPopup}
          centered
          onClose={() => setShowPopup(false)}
          actionButtonsCentered={true}
          actionButtons={[
            {
              id: 'start-campaign',
              title: 'Start',
              isPrimary: true,
              clickHandler: () =>
                onBeginCampaign(
                  (selectedAustralianState?.value ?? LicenceDetailsState.QLD) as LicenceDetailsState
                ),
            },
            {
              id: 'cancel-start-campaign',
              title: 'Cancel',
              clickHandler: () => setShowPopup(false),
            },
          ]}
          size={{ widthInPX: 600 }}
        >
          <AgxColumn>
            <AgxRadioGroup
              id={'statesRadio'}
              label={''}
              options={selectedAgencyStatesOptions}
              onValueChanged={({ value }) =>
                setSelectedAustralianState(
                  selectedAgencyStatesOptions.find((option) => option.value === value)
                )
              }
            />
          </AgxColumn>
        </Popup>
      ) : (
        <></>
      )}
    </PageLayout>
  );
};

export default CampaignsPage;
